import React from 'react';
import {
  Box, Card, CardContent, Typography, List, ListItem, ListItemText,
  ListItemAvatar, Avatar, Chip, CircularProgress, Alert
} from '@mui/material';
import {
  Work as WorkIcon, CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon, Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import apiClient from '../../../request/http-common';

const UserJobApplications = ({ userId }) => {
  const { data, isLoading, error } = useQuery(
    ['userJobApplications', userId],
    async () => {
      // If userId is provided (admin viewing), use admin endpoint, otherwise use user endpoint
      const endpoint = userId 
        ? `/api/admin/users/${userId}/job-applications`
        : `/api/user/job-applications`;
      const response = await apiClient.get(endpoint);
      return response.data;
    },
    {
      enabled: true,
      refetchInterval: 30000,
    }
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load job applications
      </Alert>
    );
  }

  const applications = data?.applications || [];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircleIcon sx={{ color: '#10B981' }} />;
      case 'rejected':
        return <CancelIcon sx={{ color: '#EF4444' }} />;
      default:
        return <ScheduleIcon sx={{ color: '#F59E0B' }} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'warning';
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
        Job Applications ({applications.length})
      </Typography>

      {applications.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 4 }}>
          <CardContent>
            <WorkIcon sx={{ fontSize: 48, color: '#6C7293', mb: 1 }} />
            <Typography variant="body2" sx={{ color: '#6C7293' }}>
              No job applications found
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <List>
          {applications.map((application) => (
            <ListItem
              key={application.id}
              sx={{
                border: '1px solid #2B3139',
                borderRadius: 1,
                mb: 1,
                background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
              }}
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: '#F7931A' }}>
                  {getStatusIcon(application.status)}
                </Avatar>
              </ListItemAvatar>

              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {application.job?.job_title}
                    </Typography>
                    <Chip
                      label={application.job?.company_name}
                      size="small"
                      sx={{ background: 'rgba(247, 147, 26, 0.2)', color: '#F7931A' }}
                    />
                    <Chip
                      label={application.status}
                      size="small"
                      color={getStatusColor(application.status)}
                    />
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" sx={{ color: '#B7BDC6', mt: 0.5 }}>
                      Applied: {new Date(application.created_at).toLocaleDateString()}
                    </Typography>
                    {application.processed_at && (
                      <Typography variant="body2" sx={{ color: '#B7BDC6', mt: 0.5 }}>
                        Processed: {new Date(application.processed_at).toLocaleDateString()}
                      </Typography>
                    )}
                    {application.admin_notes && (
                      <Typography variant="body2" sx={{ color: '#B7BDC6', mt: 0.5, fontStyle: 'italic' }}>
                        Note: {application.admin_notes}
                      </Typography>
                    )}
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default UserJobApplications;

